import axios from 'axios';

// Configuration de base
const API_URL = process.env.REACT_APP_API_URL || 'https://api.dreamdev.fr/api';

// Instance axios avec configuration par défaut
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Intercepteur pour ajouter le token JWT aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs globalement
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Le serveur a répondu avec un code d'erreur
      console.error('Erreur API:', error.response.data);
      
      // Si token expiré, déconnecter
      if (error.response.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        // Rediriger vers login si nécessaire
        if (window.location.pathname.includes('/admin')) {
          window.location.href = '/admin/login';
        }
      }
    } else if (error.request) {
      // La requête a été envoyée mais pas de réponse
      console.error('Pas de réponse du serveur:', error.request);
    } else {
      // Erreur lors de la configuration de la requête
      console.error('Erreur:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Envoyer une demande client (projet)
 * @param {Object} formData - Données du formulaire client
 * @returns {Promise} Réponse de l'API
 */
export const sendClientContact = async (formData) => {
  try {
    const response = await api.post('/contact/client', formData);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi',
    };
  }
};

/**
 * Envoyer une demande entreprise (recrutement)
 * @param {Object} formData - Données du formulaire entreprise
 * @returns {Promise} Réponse de l'API
 */
export const sendEntrepriseContact = async (formData) => {
  try {
    const response = await api.post('/contact/entreprise', formData);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi',
    };
  }
};

// ==================== ADMIN API ====================

/**
 * Login admin
 * @param {Object} credentials - { email, password }
 * @returns {Promise} Token et infos admin
 */
export const adminLogin = async (credentials) => {
  try {
    const response = await api.post('/admin/login', credentials);
    
    if (response.data.success) {
      // Stocker le token et les infos admin
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
    }
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur de connexion',
    };
  }
};

/**
 * Logout admin
 */
export const adminLogout = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
};

/**
 * Vérifier si l'admin est connecté
 * @returns {Promise} Infos admin
 */
export const getAdminProfile = async () => {
  try {
    const response = await api.get('/admin/me');
    return {
      success: true,
      data: response.data.admin,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Non authentifié',
    };
  }
};

/**
 * Récupérer toutes les demandes avec filtres
 * @param {Object} params - { type, status, search, page, limit, sortBy, order }
 * @returns {Promise} Liste des demandes
 */
export const getAllContacts = async (params = {}) => {
  try {
    const response = await api.get('/admin/contacts', { params });
    return {
      success: true,
      data: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
    };
  }
};

/**
 * Récupérer une demande par ID
 * @param {string} id - ID de la demande
 * @returns {Promise} Détails de la demande
 */
export const getContactById = async (id) => {
  try {
    const response = await api.get(`/admin/contacts/${id}`);
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Demande introuvable',
    };
  }
};

/**
 * Mettre à jour le statut d'une demande
 * @param {string} id - ID de la demande
 * @param {string} status - Nouveau statut (nouveau, en_cours, traite, archive)
 * @returns {Promise} Demande mise à jour
 */
export const updateContactStatus = async (id, status) => {
  try {
    const response = await api.patch(`/admin/contacts/${id}/status`, { status });
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la mise à jour',
    };
  }
};

/**
 * Supprimer une demande
 * @param {string} id - ID de la demande
 * @returns {Promise} Confirmation
 */
export const deleteContact = async (id) => {
  try {
    const response = await api.delete(`/admin/contacts/${id}`);
    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la suppression',
    };
  }
};

/**
 * Récupérer les statistiques
 * @returns {Promise} Stats
 */
export const getStats = async () => {
  try {
    const response = await api.get('/admin/stats');
    return {
      success: true,
      data: response.data.stats,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération des stats',
    };
  }
};

/**
 * Tester la connexion à l'API
 * @returns {Promise} Statut de l'API
 */
export const testConnection = async () => {
  try {
    const response = await api.get('/');
    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: 'Impossible de se connecter à l\'API',
    };
  }
};

// ==================== COFFEE API ====================

/**
 * Récupérer le compteur de cafés
 * @returns {Promise} Nombre de cafés
 */
export const getCoffeeCount = async () => {
  try {
    const response = await api.get('/coffee');
    return {
      success: true,
      count: response.data.count,
    };
  } catch (error) {
    return {
      success: false,
      count: 0,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
    };
  }
};

/**
 * Incrémenter le compteur de cafés
 * @returns {Promise} Nouveau nombre de cafés
 */
export const incrementCoffee = async () => {
  try {
    const response = await api.post('/coffee/increment');
    return {
      success: true,
      count: response.data.count,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de l\'incrémentation',
    };
  }
};

// ==================== PAYMENT API ====================

/**
 * Créer un lien de paiement Stripe
 * @param {Object} paymentData - Données du paiement
 * @returns {Promise}
 */
export const createPaymentLink = async (paymentData) => {
  try {
    const response = await api.post('/admin/payments', paymentData);
    return {
      success: true,
      payment: response.data.payment,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la création',
    };
  }
};

/**
 * Récupérer tous les paiements
 * @param {Object} filters - Filtres (status, search, page, limit)
 * @returns {Promise}
 */
export const getAllPayments = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/admin/payments?${params}`);
    return {
      success: true,
      payments: response.data.payments,
      pagination: response.data.pagination,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
    };
  }
};

/**
 * Récupérer un paiement par ID
 * @param {string} paymentId
 * @returns {Promise}
 */
export const getPaymentById = async (paymentId) => {
  try {
    const response = await api.get(`/admin/payments/${paymentId}`);
    return {
      success: true,
      payment: response.data.payment,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
    };
  }
};

/**
 * Mettre à jour le statut d'un paiement
 * @param {string} paymentId
 * @param {string} status
 * @returns {Promise}
 */
export const updatePaymentStatus = async (paymentId, status) => {
  try {
    const response = await api.patch(`/admin/payments/${paymentId}/status`, { status });
    return {
      success: true,
      payment: response.data.payment,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la mise à jour',
    };
  }
};

/**
 * Supprimer un paiement
 * @param {string} paymentId
 * @returns {Promise}
 */
export const deletePayment = async (paymentId) => {
  try {
    const response = await api.delete(`/admin/payments/${paymentId}`);
    return {
      success: true,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la suppression',
    };
  }
};

/**
 * Récupérer les statistiques des paiements
 * @returns {Promise}
 */
export const getPaymentStats = async () => {
  try {
    const response = await api.get('/admin/payments/stats');
    return {
      success: true,
      stats: response.data.stats,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
    };
  }
};

// ==================== CHECKOUT PUBLIC API ====================

/**
 * Récupérer les détails d'un paiement/commande pour le suivi (public)
 * @param {string} paymentId
 * @returns {Promise}
 */
export const getOrderTracking = async (paymentId) => {
  try {
    const response = await api.get(`/payments/checkout/${paymentId}`);
    return {
      success: true,
      payment: response.data.payment,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
    };
  }
};

// Alias pour compatibilité
export const getPaymentForCheckout = getOrderTracking;

/**
 * Accepter les conditions et créer le lien Stripe (public)
 * @param {string} paymentId
 * @returns {Promise}
 */
export const acceptPaymentAndPay = async (paymentId) => {
  try {
    const response = await api.post(`/payments/checkout/${paymentId}/accept`);
    return {
      success: true,
      stripeUrl: response.data.stripeUrl,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors du traitement',
    };
  }
};

/**
 * Mettre à jour la progression d'un projet (admin)
 * @param {string} paymentId
 * @param {number} currentStep
 * @param {string} stepStatus
 * @param {string} notes
 * @returns {Promise}
 */
export const updateProjectProgress = async (paymentId, currentStep, stepStatus, notes = '') => {
  try {
    const response = await api.patch(`/admin/payments/${paymentId}/progress`, {
      currentStep,
      stepStatus,
      notes
    });
    return {
      success: true,
      payment: response.data.payment,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la mise à jour',
    };
  }
};

/**
 * Ajouter une mise à jour pour le client (admin)
 * @param {string} paymentId
 * @param {string} title
 * @param {string} message
 * @returns {Promise}
 */
export const addProjectUpdate = async (paymentId, title, message) => {
  try {
    const response = await api.post(`/admin/payments/${paymentId}/updates`, {
      title,
      message
    });
    return {
      success: true,
      payment: response.data.payment,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de l\'ajout',
    };
  }
};

export default api;